import { useMutation } from '@tanstack/react-query'
import { BooleanParam, createEnumParam, NumberParam, withDefault } from 'use-query-params'

import useFiltering from '@shared/hooks/src/useFiltering'
import useQuery from '@shared/hooks/src/useQuery'
import { queryClient, setQueriesData, setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { QK } from '@shared/utils'

/**
 * Hook to retrieve a patients insurances.
 */
export function useInsurances(patientId, query, options = {}) {
  return useQuery({
    queryKey: QK.patients.id(patientId).insurances.list(query),
    queryFn: () => API.patients.id(patientId).insurances.list(query),
    ...options,
  })
}

/**
 * Hook to retrieve a the photos for an insurance record
 */
export function useInsurance(patientId, insuranceId, options = {}) {
  return useQuery({
    queryKey: QK.patients.id(patientId).insurances.id(insuranceId).details,
    queryFn: () => API.patients.id(patientId).insurances.id(insuranceId).details(),
    ...options,
  })
}

/**
 * Add an insurance record.
 * @param {string} patientId - the patient id
 */
export function useInsuranceCreation(patientId) {
  return useMutation({
    mutationFn: (data) => API.patients.id(patientId).insurances.create(data),
    onSuccess: (insurance) => {
      setQueryData(QK.patients.id(patientId).insurances.id(insurance.id).details, insurance)

      queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).insurances.lists })
      queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).insurances.summary })
      queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).details })
    },
  })
}

/**
 * Hook to remove a patient insurance.
 * @param {string} patientId - the patient id
 */
export function useInsuranceRemove(patientId) {
  return useMutation({
    mutationFn: (insuranceId) => API.patients.id(patientId).insurances.id(insuranceId).remove(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).insurances.lists })
      queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).insurances.summary })
    },
  })
}

/**
 * Hook to update patient's insurance.
 *
 * @param {string} patientId - the patient id
 * @param {string} insuranceId - the insurance id
 */
export function useInsuranceUpdate(patientId, insuranceId) {
  return useMutation({
    mutationFn: (data) => API.patients.id(patientId).insurances.id(insuranceId).update(data),
    onSuccess: (insurance) => {
      setQueryData(QK.patients.id(patientId).insurances.id(insuranceId).details, insurance)

      setQueriesData({ queryKey: QK.patients.id(patientId).insurances.lists }, (prev = []) =>
        prev.map((item) => (item.id === insurance.id ? insurance : item))
      )

      queryClient.invalidateQueries({ queryKey: QK.patients.id(patientId).insurances.summary })
    },
  })
}

const SortParam = createEnumParam(['asc', 'desc'])

/**
 * Hook to set and retrieve the insurances filters.
 */
export function useInsurancesFilters() {
  return useFiltering({
    insurancesExpanded: withDefault(BooleanParam, false),
    insurancesSort: withDefault(SortParam, 'desc'),
    insDetailed: NumberParam,
  })
}
